import React, { useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, LayoutGrid, Plus, Compass, Bot, Code2, Clock } from 'lucide-react';
import { useChatStoreContext } from '../context/ChatStoreContext';

interface MobileNavProps {
  onOpenUserSidebar?: () => void;
}

interface NavItem {
  id: string;
  path?: string;
  icon: React.ElementType;
  label: string;
  isAction?: boolean;
}

export const MobileNav: React.FC<MobileNavProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { createNewChat } = useChatStoreContext();

  const currentPath = location.pathname;

  const navItems: NavItem[] = useMemo(() => [
    { id: 'home', path: '/', icon: Home, label: 'Chat' },
    { id: 'plugins', path: '/plugins', icon: LayoutGrid, label: 'Plugins' },
    { id: 'new', icon: Plus, label: 'New', isAction: true },
    { id: 'explore', path: '/explore', icon: Compass, label: 'Explore' },
    { id: 'swarm', path: '/swarm', icon: Bot, label: 'Swarm' },
    { id: 'code', path: '/code', icon: Code2, label: 'Code' },
    { id: 'tasks', path: '/scheduled-tasks', icon: Clock, label: 'Tasks' },
  ], []);

  const handleItemClick = useCallback((item: NavItem) => {
    if (item.isAction) {
      createNewChat();
      navigate('/');
    } else if (item.path) {
      navigate(item.path);
    }
  }, [createNewChat, navigate]);

  const isActive = useCallback((item: NavItem) => {
    if (item.isAction) return false;
    return currentPath === item.path;
  }, [currentPath]);

  return (
    <nav 
      className="fixed bottom-0 left-0 right-0 z-50 bg-[#121214]/95 backdrop-blur-xl border-t border-[#2a2a2e] md:hidden"
      aria-label="Mobile navigation"
    >
      <div 
        className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory"
        style={{ 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none'
        }}
      >
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = isActive(item);
          
          return (
            <motion.button 
              key={item.id} 
              whileTap={{ scale: 0.9 }}
              onClick={() => handleItemClick(item)}
              className={`
                flex flex-col items-center justify-center gap-1 py-2 px-4 min-w-[72px] snap-start 
                transition-colors relative
                ${active ? 'text-white' : 'text-[#ddd] hover:text-[#999]'}
              `}
              aria-label={item.label}
              aria-current={active ? 'page' : undefined}
            >
              {active && (
                <motion.div 
                  layoutId="mobileNavIndicator" 
                  className="absolute -top-0.5 w-1 h-1 rounded-full bg-blue-500"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }} 
                />
              )}
              
              <div 
                className={`
                  relative flex items-center justify-center transition-all
                  ${item.isAction 
                    ? 'w-10 h-10 -mt-5 rounded-full bg-blue-500 text-white shadow-lg' 
                    : 'w-6 h-6'
                  }
                `}
                style={item.isAction ? { boxShadow: '0 4px 20px rgba(59,130,246,0.4)' } : undefined}
              >
                <Icon size={20} aria-hidden="true" />
              </div>
              
              <span className="text-[11px] font-medium whitespace-nowrap">
                {item.label}
              </span>
            </motion.button>
          );
        })}
      </div>
      
      {/* Safe area spacer for notched devices */}
      <div className="h-[env(safe-area-inset-bottom)]" />
    </nav>
  );
};

export default MobileNav;